
import { useState } from "react";
import { useCreatorTransactions } from "@/hooks/useCreatorTransactions";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { AdminUsersSearch } from "@/components/admin/AdminUsersSearch";

export const CreatorEarnings = () => {
  const { creators, isLoading } = useCreatorTransactions();
  const [selectedCreator, setSelectedCreator] = useState<any>(null);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [search, setSearch] = useState("");

  const handlePayout = async () => {
    try {
      setIsProcessing(true);
      const amount = parseFloat(payoutAmount);

      if (isNaN(amount) || amount <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }

      if (amount > selectedCreator.earnings) {
        toast.error("Payout amount cannot exceed available earnings");
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.rpc('record_manual_payout', {
        _creator_id: selectedCreator.id,
        _amount: amount,
        _paid_by: user.id,
        _notes: payoutNotes,
      });

      if (error) throw error;

      toast.success("Payout recorded successfully");
      setShowPayoutDialog(false);
      setPayoutAmount("");
      setPayoutNotes("");
    } catch (error) {
      console.error("Error processing payout:", error);
      toast.error("Failed to process payout");
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return <div>Loading creator earnings...</div>;
  }

  const filteredCreators = creators?.filter(creator =>
    creator.username.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Creator Earnings</h2>
      <div className="mb-6">
        <AdminUsersSearch search={search} setSearch={setSearch} />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Creator</TableHead>
            <TableHead>Total Earned</TableHead>
            {/* <TableHead>Current Balance</TableHead> */}
            <TableHead>Monthly Subs</TableHead>
            <TableHead>Lifetime Subs</TableHead>
            {/* <TableHead>Actions</TableHead> */}
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredCreators.map((creator) => {
            const monthlySubs = creator.subscription_transactions.filter(
              tx => tx.subscription_type === 'monthly' && tx.status === 'completed'
            ).length;
            
            const lifetimeSubs = creator.subscription_transactions.filter(
              tx => tx.subscription_type === 'lifetime' && tx.status === 'completed'
            ).length;

            const totalEarned = creator.subscription_transactions.reduce(
              (sum, tx) => sum + (tx.amount || 0),
              0
            ) * 0.85; // Platform takes 15%

            return (
              <TableRow key={creator.id}>
                <TableCell>{creator.username}</TableCell>
                <TableCell>${totalEarned.toFixed(2)}</TableCell>
                {/* <TableCell>${creator.earnings.toFixed(2)}</TableCell> */}
                <TableCell>{monthlySubs}</TableCell>
                <TableCell>{lifetimeSubs}</TableCell>
                {/* <TableCell>
                  <button
                    className="text-blue-500 hover:underline mr-2"
                    onClick={() => {
                      setSelectedCreator(creator);
                      setShowPayoutDialog(true);
                    }}
                    disabled={creator.earnings <= 0}
                  >
                    Record Payout
                  </button>
                </TableCell> */}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Manual Payout</DialogTitle>
            <DialogDescription>
              Record a manual payout for {selectedCreator?.username}. Current
              balance: ${selectedCreator?.earnings.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter payout amount"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
                placeholder="Add notes about this payout"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              className="bg-gray-200 px-4 py-2 rounded"
              onClick={() => setShowPayoutDialog(false)}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={handlePayout}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Record Payout"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
